// @flow
import * as React from "react";
import { withRouter } from "react-router";
import { adopt } from "react-adopt";

import { denormalizeJsonApi } from "buba-components/src/utils/denormalizeJsonApi";
import DataFetcher from "buba-components/src/components/DataFetcher";
import InquiryPage from "buba-components/src/components/InquiryPage";
import DownloadBanner from "buba-components/src/components/DownloadBanner";
import { FlashMessageConsumer } from "buba-components";
import {
  USER_TYPE__CUSTOMER,
  USER_TYPE__BANK_ADVISOR,
  USER_TYPE__GUARANTEE_AGENT
} from "buba-components/src/utils/userType";
import { INQUIRY_TYPE__BANK_ADVISOR } from "buba-components/src/utils/inquiryType";
import statusErrors from "buba-components/src/data/statusErrors";
import { saveFileFromResponse } from "buba-components/src/utils/saveFile";
import paths from "../../data/paths";
import { bankInquiryTabsNew } from "../../data/navLinks";
import {
  getInquiryV2,
  getFileRequestsV2,
  downloadFileV2,
  downloadSummaryPdfV2,
  downloadGuaranteeCertificateV2
} from "../../api/v2actions";
import BankInquiryDownloadsNew from "./BankInquiryDownloadsNew";
import BankInquiryUploadsNew from "./BankInquiryUploadsNew";

export type UploadedFile = {
  bankName: ?string,
  description: ?string,
  downloadedByBankAdvisorAt: ?string,
  downloadedByCustomerAt: ?string,
  downloadedByGuaranteeAgentAt: ?string,
  filename: string,
  hiddenFor: typeof USER_TYPE__CUSTOMER | typeof USER_TYPE__BANK_ADVISOR,
  id: string,
  uploadedBy:
    | typeof USER_TYPE__GUARANTEE_AGENT
    | typeof USER_TYPE__CUSTOMER
    | typeof USER_TYPE__BANK_ADVISOR
};

export type RequestedFile = {
  description: string,
  fileKey: string,
  id: string,
  requestedFrom: typeof USER_TYPE__CUSTOMER | typeof USER_TYPE__GUARANTEE_AGENT
};

export type RequestedFiles = RequestedFile[];
export type UploadedFiles = UploadedFile[];

type InquiryParams = { inquiryId: number, render: () => React.Node };

type FileDataParams = {
  inquiry: number,
  message: { addMessage: (msg: string, type: string) => void },
  render: () => React.Node
};
type BankInquiryDocumentsProps = {
  match: {
    params: { id: number }
  }
};

type SetBankInquiryDocumentsContents = {
  addMessage: (msg: string, type: string) => void,
  inquiry: {
    data: { process: { id: number } }
  },
  fileData: {
    data: {
      inquiryProcessUploadedFiles: UploadedFiles,
      fileRequests: RequestedFiles
    },
    requestFetch: any => Promise<any>
  }
};

const Composed = adopt({
  message: <FlashMessageConsumer />,
  inquiry: ({ inquiryId, render }: InquiryParams) => (
    <DataFetcher
      apiAction={getInquiryV2}
      mapper={denormalizeJsonApi}
      initialPayload={{ id: Number(inquiryId) }}
      redirectOnError
    >
      {render}
    </DataFetcher>
  ),
  fileData: ({ inquiry, message: { addMessage }, render }: FileDataParams) => {
    if (inquiry.isLoading) {
      return null;
    }

    const inquiryId = inquiry.data.id;

    return (
      <DataFetcher
        apiAction={getFileRequestsV2}
        mapper={denormalizeJsonApi}
        initialPayload={{ id: Number(inquiryId) }}
        onError={msg => {
          addMessage(msg, "failure");
        }}
        redirectOnError
      >
        {render}
      </DataFetcher>
    );
  }
});

const setBankInquiryDocumentsContents = ({
  addMessage,
  inquiry,
  fileData
}: SetBankInquiryDocumentsContents) => {
  const inquiryId: string = inquiry.data.id;
  const summaryPdfsAvailable = inquiry.data?.fidesMetadata?.requestPdfsAvailable;
  const isGuaranteeCertificateAvailable = inquiry.data?.fidesMetadata?.certificatesAvailable;
  const inquiryProcessUploadedFiles: UploadedFiles = inquiry.data.financingRequest.fileUploads;
  const inquiryProcessUploadedFilesByBankAdvisor = inquiryProcessUploadedFiles.filter(
    ({ uploadedBy }) => uploadedBy.type === INQUIRY_TYPE__BANK_ADVISOR
  );

  const fileRequests: RequestedFiles = fileData.data || [];

  const handleInquiryFormDownload = async () => {
    try {
      const res = await downloadSummaryPdfV2(inquiryId);
      saveFileFromResponse(res, inquiryId);
    } catch (err) {
      addMessage(statusErrors(err.status), "failure");
    }
  };

  const handleGuaranteeCertificateDownload = async () => {
    try {
      const res = await downloadGuaranteeCertificateV2(inquiryId);
      saveFileFromResponse(res, `Bürgschaftserklärung – ${inquiryId}`);
    } catch (err) {
      addMessage(statusErrors(err.status), "failure");
    }
  };

  const documentsProps = {
    refetchFiles: inquiry.requestFetch || null,
    inquiryProcessId: inquiryId,
    onError: (statusCode: number) => addMessage(statusErrors(statusCode), "failure"),
    downloadAction: file => {
      return downloadFileV2(inquiryId, file.id);
    }
  };
  return (
    <>
      {isGuaranteeCertificateAvailable && (
        <DownloadBanner
          handleDownload={handleGuaranteeCertificateDownload}
          text="Bürgschaftserklärung"
          buttonLabel="Herunterladen"
        />
      )}
      {summaryPdfsAvailable && (
        <DownloadBanner
          handleDownload={handleInquiryFormDownload}
          text="Antragsdokumente"
          buttonLabel="Herunterladen"
        />
      )}
      <BankInquiryDownloadsNew
        {...documentsProps}
        fileRequests={fileRequests}
        uploadedFiles={inquiryProcessUploadedFiles}
        addMessage={addMessage}
        inquiryProcessId={inquiryId}
      />
      <BankInquiryUploadsNew
        {...documentsProps}
        fileRequests={fileRequests}
        inquiryProcessUploadedFiles={inquiryProcessUploadedFilesByBankAdvisor}
      />
    </>
  );
};

const BankInquiryDocuments = ({
  match: {
    params: { id: inquiryId }
  }
}: BankInquiryDocumentsProps) => (
  <Composed inquiryId={inquiryId}>
    {({ inquiry, fileData, message: { addMessage } }) => {
      const isLoading = inquiry.isLoading || fileData.isLoading;
      const companyName: string = inquiry.data.company.name;
      return (
        <InquiryPage
          backPath={paths.bankInquiriesList}
          companyName={companyName}
          isLoadingData={isLoading}
          issuedOn={inquiry.data ? new Date(inquiry.data.createdAt) : null}
          tabs={id => bankInquiryTabsNew(id, inquiry.data)}
        >
          {setBankInquiryDocumentsContents({
            isLoading,
            addMessage,
            inquiry,
            fileData
          })}
        </InquiryPage>
      );
    }}
  </Composed>
);

export default withRouter(BankInquiryDocuments);
